import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { api } from '../lib/api';

export default function DynamicForm({ slug, lang }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState({});

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/forms/${slug}`);
        setForm(response.data);
      } catch (error) {
        console.error('Failed to fetch form', error);
        setForm(null);
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [slug]);

  const handleChange = (fieldId, value) => {
    setPayload((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/forms/${slug}/submit`, payload);
      const message = lang === 'en' && form?.submit_message_en ? form.submit_message_en : form?.submit_message;
      toast.success(message || 'Form submitted');
      setPayload({});
    } catch (error) {
      console.error('Form submit failed', error);
      toast.error('Submission failed');
    }
  };

  if (loading) {
    return <p className="text-white/60">Loading...</p>;
  }

  if (!form) {
    return <p className="text-white/60">Form not available.</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-bold font-heading" style={{ fontSize: 'calc(clamp(1.5rem, 3vw, 1.875rem) * var(--section-title-scale, 1))', color: 'var(--site-heading-color, #FFFFFF)' }}>
        {lang === 'en' && form.title_en ? form.title_en : form.title}
      </h3>
      {form.fields.map((field) => (
        <div key={field.id} className="space-y-2">
          <Label style={{ color: 'var(--site-heading-color, #FFFFFF)' }}>
            {lang === 'en' && field.label_en ? field.label_en : field.label}
          </Label>
          {field.type === 'textarea' ? (
            <Textarea
              value={payload[field.id] || ''}
              onChange={(event) => handleChange(field.id, event.target.value)}
              className="bg-zinc-900/50 border-white/10 text-white"
              style={{ background: 'var(--site-form-bg, rgba(24,24,27,0.5))', color: 'var(--site-form-text, #FFFFFF)', borderColor: 'var(--site-form-border, rgba(255,255,255,0.1))' }}
              rows={4}
              required={field.required}
            />
          ) : field.type === 'select' ? (
            <select
              value={payload[field.id] || ''}
              onChange={(event) => handleChange(field.id, event.target.value)}
              className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2 w-full"
              style={{ background: 'var(--site-form-bg, rgba(24,24,27,0.5))', color: 'var(--site-form-text, #FFFFFF)', borderColor: 'var(--site-form-border, rgba(255,255,255,0.1))' }}
              required={field.required}
            >
              <option value="">Select...</option>
              {((lang === 'en' && field.options_en?.length) ? field.options_en : field.options || []).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <Input
              type={field.type || 'text'}
              value={payload[field.id] || ''}
              onChange={(event) => handleChange(field.id, event.target.value)}
              className="bg-zinc-900/50 border-white/10 text-white"
              style={{ background: 'var(--site-form-bg, rgba(24,24,27,0.5))', color: 'var(--site-form-text, #FFFFFF)', borderColor: 'var(--site-form-border, rgba(255,255,255,0.1))' }}
              required={field.required}
            />
          )}
        </div>
      ))}
      <Button type="submit" className="rounded-xl" style={{ background: 'var(--site-button-bg, #E11D2E)', color: 'var(--site-button-text, #FFFFFF)' }}>
        Submit
      </Button>
    </form>
  );
}
